// Cue copy/paste (22 Oct 2026; ~/Code/CUELIST_CUE_COPY_PASTE_2026-10-22.md). The copy keeps the label and the whole
// data map (not rebuilt from Utils.cueAttributes, which would drop followHangType); the paste gets a new id, the
// current layer, a cleared number (addCue's Auto Number fills it or leaves it blank), and the spot you clicked.
import { DRAWING_FIELDS } from './cue-drawing-fields.js'; // eslint-disable-line import/extensions

const DROPPED = ['id', 'layer', 'createdAt', 'editedAt', 'legacyBackup', 'x', 'y', 'pageNumber', ...DRAWING_FIELDS];

export function cueForPaste(cue) {
    const out = JSON.parse(JSON.stringify(cue || {}));
    for (const key of DROPPED) {
        delete out[key];
    }
    out.title = '';
    return out;
}

// The paster's own look for one cue lives in their users/{uid}.projects.{pid} maps, keyed by cue id. A moved box's
// position (floatingCues) is left out: the pasted box sits beside its new marker.
const LOOK_MAPS = ['headerBoxOptions', 'userDefinedOptions', 'titleFontSizeOptions', 'cueLineOpacityOptions', 'cueBoxOpacityOptions'];

export function userOptionsForPaste(projectSettings, projectId, fromId, toId) {
    const update = {};
    for (const map of LOOK_MAPS) {
        const value = projectSettings && projectSettings[map] && projectSettings[map][fromId];
        if (value !== undefined) {
            update['projects.' + projectId + '.' + map + '.' + toId] = value;
        }
    }
    return update;
}

// Cmd-C / Cmd-V inside a text field (a cue's number or label, List view, a search box) stay plain text copy/paste.
export function isTypingTarget(el) {
    return !!el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');
}
// Josh's testing, 22 Oct afternoon. Only a press on a cue box or cue marker keeps a cue picked for Cmd-C; anything else
// (a structure mark, the script, a note, the toolbar) drops it, so "Cue copied" never follows another press.
const CUE_PRESS = '.ql-cue, .ql-position-cue-marker, .ql-selection-cue-marker';

export function isCuePress(el) {
    return !!el && typeof el.closest === 'function' && !!el.closest(CUE_PRESS);
}

// The paste spot (cues, notes, structure marks) is the last press anywhere on a page, words included: pdf.js puts each
// run of script text in its own span, which the old check (the text layer itself or a canvas) missed. Pressing a cue,
// structure mark or note picks it, so it leaves the spot where it was. The pressed element is kept so note paste can
// find its page.
const NOT_A_SPOT = CUE_PRESS + ', .ql-script-marker, .ql-note-container';

export function pasteSpotFrom(e) {
    const el = e && e.target;
    const page = el && typeof el.closest === 'function' && el.closest('.ql-page');
    if (!page || el.closest(NOT_A_SPOT)) return null;
    const spot = { clientX: e.clientX, clientY: e.clientY, target: el, page, fx: null, fy: null };
    // Where on the page, as fractions of its size, so a scroll or zoom before Cmd-V doesn't move the paste.
    const r = typeof page.getBoundingClientRect === 'function' && page.getBoundingClientRect();
    if (r && r.width > 0 && r.height > 0) {
        spot.fx = (e.clientX - r.left) / r.width;
        spot.fy = (e.clientY - r.top) / r.height;
    }
    return spot;
}

// The paste spot in screen coordinates as of now: the same place on the pressed page wherever it has scrolled to. If
// that page is gone (page turn, re-render), the screen spot of the press, as before, with the element now under it.
export function resolvePasteSpot(spot, doc) {
    if (!spot) return null;
    const { page } = spot;
    if (page && page.isConnected && spot.fx !== null && spot.fy !== null) {
        const r = page.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
            return {
                clientX: r.left + (spot.fx * r.width),
                clientY: r.top + (spot.fy * r.height),
                target: spot.target && spot.target.isConnected ? spot.target : page
            };
        }
    }
    const under = doc && typeof doc.elementFromPoint === 'function' && doc.elementFromPoint(spot.clientX, spot.clientY);
    return { clientX: spot.clientX, clientY: spot.clientY, target: under || spot.target };
}

// Structure marks (Josh, 22 Oct evening): a pressed mark is picked for Cmd-C until something else is pressed. The copy
// keeps the type and the label (its name and number); where it lands sets its page, position and act.
export function isMarkPress(el) {
    return !!el && typeof el.closest === 'function' && !!el.closest('.ql-script-marker');
}

export function markForPaste(mark) {
    return mark ? { type: mark.type, label: mark.label } : null;
}


// WEBPACK FOOTER //
// ./src/lib/cue-paste.js